import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { publicApi } from "@/lib/api";

interface City {
  id: number;
  name: string;
  state: string;
  country: string;
}

interface PopularRoute {
  id: number;
  originCity: City;
  destinationCity: City;
  oneWayPrice: string;
  roundTripPrice: string;
}

interface PopularRoutesResponse {
  routes: PopularRoute[];
}

/**
 * Editorial grid of popular routes.
 *
 * Replaces the 6-gradient rainbow carousel with calm, photography-first cards
 * using tokens, tabular pricing, and Fraunces destination names.
 */
export function PopularRoutes() {
  const { t, i18n } = useTranslation();
  const { slug } = useTenant();

  const { data, isLoading, error } = useQuery({
    queryKey: ["popular-routes", slug],
    queryFn: async () => {
      if (!slug) return { routes: [] };
      return publicApi.get<PopularRoutesResponse>(
        `/api/public/${slug}/popular-routes`,
      );
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  const routes = data?.routes || [];

  const formatPrice = useCallback(
    (price: string) =>
      new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(parseFloat(price)),
    [i18n.language],
  );

  const getRouteImagePath = useCallback(
    (origin: string, destination: string): string | null => {
      if (!slug) return null;
      const cityToKey: Record<string, string> = {
        "Dolores Hidalgo": "Dolores",
        "San Miguel de Allende": "SanMiguel",
        "San Felipe": "SanFelipe",
        "San Luis Potosí": "SanLuisPotosi",
      };
      const originKey = origin.replace(/\s+/g, "");
      const destKey = cityToKey[destination] || destination.replace(/\s+/g, "");
      return `/images/tenants/${slug}/routes/${originKey}-${destKey}.jpg`;
    },
    [slug],
  );

  if (isLoading) {
    return (
      <section className="relative py-20 md:py-24 bg-white border-b hairline-strong">
        <div className="mx-auto max-w-6xl px-6">
          <SectionEyebrow label={t("landing.popularRoutes", "Popular Routes")} />
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="aspect-[4/5] bg-stone-100 border hairline animate-pulse"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || routes.length === 0) return null;

  return (
    <section
      id="popular-routes"
      className="relative py-20 md:py-24 bg-white border-b hairline-strong scroll-mt-20"
      aria-labelledby="popular-routes-heading"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <SectionEyebrow label={t("landing.discover", "Routes")} />
            <h2
              id="popular-routes-heading"
              className="font-display text-display-md text-ink mt-3 max-w-xl"
            >
              {t("landing.popularRoutes", "Where we go")}
            </h2>
          </div>
        </div>

        <ul className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {routes.map((route) => {
            const img = getRouteImagePath(
              route.originCity.name,
              route.destinationCity.name,
            );
            return (
              <li key={route.id}>
                <a
                  href="#top"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="group block focus-visible-ring"
                >
                  <figure className="relative aspect-[4/5] overflow-hidden border hairline bg-stone-100">
                    {img && (
                      <img
                        src={img}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    {/* Tenant brand accent — a thin ink bar at bottom */}
                    <div
                      className="absolute inset-x-0 bottom-0 h-1 bg-brand opacity-80 group-hover:h-1.5 transition-[height]"
                      aria-hidden="true"
                    />
                  </figure>
                  <figcaption className="mt-5 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-ink-subtle text-eyebrow uppercase">
                        <span>{route.originCity.name}</span>
                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </div>
                      <h3 className="font-display text-[1.5rem] md:text-[1.75rem] leading-tight text-ink mt-1 truncate">
                        {route.destinationCity.name}
                      </h3>
                      <p className="text-xs text-ink-subtle mt-0.5 tabular">
                        {route.destinationCity.state}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-eyebrow uppercase text-ink-subtle">
                        {t("landing.from", "from")}
                      </div>
                      <div className="font-display text-[1.25rem] md:text-[1.4rem] text-ink tabular mt-0.5">
                        {formatPrice(route.oneWayPrice)}
                      </div>
                    </div>
                  </figcaption>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-2 w-2 bg-brand" aria-hidden="true" />
      <span className="text-eyebrow uppercase text-ink-muted tabular">
        {label}
      </span>
      <span
        className="flex-1 h-px bg-hairline-strong max-w-[6rem]"
        aria-hidden="true"
      />
    </div>
  );
}

export default PopularRoutes;
